import React, {useState} from 'react';
import Dialog from './Dialog';
import {CheckCheckIcon, DownloadIcon, ShieldIcon} from './icons';

interface RecoveryCodesModalProps {
    codes: string[];
    /** Закрытие возможно только после явного подтверждения «я сохранил коды». */
    onClose: () => void;
}

/**
 * Одноразовый показ recovery-кодов после включения 2FA. На сервере остаются только
 * хэши, поэтому повторно коды получить нельзя — модалка требует подтверждения.
 */
const RecoveryCodesModal: React.FC<RecoveryCodesModalProps> = ({codes, onClose}) => {
    const [copied, setCopied] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const copyAll = async () => {
        try {
            await navigator.clipboard.writeText(codes.join('\n'));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Клипборд недоступен (например, http) — пользователь скопирует вручную.
        }
    };

    const downloadTxt = () => {
        const blob = new Blob(
            ['Recovery-коды двухфакторной аутентификации Clothes\n\n' + codes.join('\n') + '\n'],
            {type: 'text/plain;charset=utf-8'}
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'clothes-recovery-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    // Уйти «мимо» решения нельзя: коды больше не покажут, на сервере только хэши,
    // поэтому окно закрывается лишь после отметки «я сохранил коды».
    return (
        <Dialog
            open
            onClose={onClose}
            title="Сохраните recovery-коды"
            dismissible={confirmed}
            showClose={false}
            data-testid="recovery-codes-modal"
            overlayClassName="fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            panelClassName="relative z-10 w-full max-w-md card p-6 outline-none"
            headerClassName=""
            headerInnerClassName="block"
            bodyClassName=""
            icon={
                <div className="w-12 h-12 rounded-2xl bg-ink-900 text-white dark:bg-white dark:text-ink-900 flex items-center justify-center mb-4">
                    <ShieldIcon className="w-6 h-6"/>
                </div>
            }
        >
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Каждый код можно использовать один раз вместо кода из приложения — например,
                    при потере телефона. Коды показываются <span className="font-medium text-gray-900 dark:text-gray-100">только сейчас</span>.
                </p>

                <ul className="mt-4 grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-ink-900 rounded-xl font-mono text-sm text-gray-900 dark:text-gray-100"
                    data-testid="recovery-codes">
                    {codes.map(code => (
                        <li key={code}>{code}</li>
                    ))}
                </ul>

                <div className="mt-4 flex gap-2">
                    <button type="button" onClick={copyAll} className="btn-secondary flex-1 min-h-[44px]">
                        {copied ? (
                            <><CheckCheckIcon className="w-4 h-4"/> Скопировано</>
                        ) : (
                            <>Копировать</>
                        )}
                    </button>
                    <button type="button" onClick={downloadTxt} className="btn-secondary flex-1 min-h-[44px]">
                        <DownloadIcon className="w-4 h-4"/> Скачать .txt
                    </button>
                </div>

                <label className="mt-4 flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    />
                    <span>Я сохранил коды в надёжном месте</span>
                </label>

                <button
                    type="button"
                    onClick={onClose}
                    disabled={!confirmed}
                    className="btn-primary w-full mt-4 min-h-[44px]"
                >
                    Готово
                </button>
        </Dialog>
    );
};

export default RecoveryCodesModal;
